/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import { highlightMessages as messages } from '@atlaskit/editor-common/messages';
import { getInputMethodFromParentKeys } from '@atlaskit/editor-common/toolbar';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { REMOVE_HIGHLIGHT_COLOR } from '@atlaskit/editor-common/ui-color';
import type { TextColorPlugin } from '@atlaskit/editor-plugin-text-color';
import { useToolbarDropdownMenu } from '@atlaskit/editor-toolbar';
import type { ToolbarComponentTypes } from '@atlaskit/editor-toolbar-model';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

const styles = cssMap({
	removeColorButton: {
		marginTop: token('space.075'),
		marginInline: token('space.025'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		borderRadius: token('radius.small'),
	},
});

type RemoveColorSectionProps = {
	api?: ExtractInjectionAPI<TextColorPlugin>;
	parents: ToolbarComponentTypes;
};

export const RemoveColorSection = ({ api, parents }: RemoveColorSectionProps): JSX.Element => {
	const { formatMessage } = useIntl();
	const context = useToolbarDropdownMenu();
	const closeMenu = context?.closeMenu;

	const onClick = (event: React.MouseEvent) => {
		if (!api) {
			return;
		}
		const defaultColor = api.textColor.sharedState.currentState()?.defaultColor;

		if (!defaultColor) {
			return;
		}

		api.core.actions.execute(({ tr }) => {
			api.textColor.commands.changeColor(
				defaultColor,
				getInputMethodFromParentKeys(parents),
			)({ tr });

			api.highlight?.commands.changeColor({
				color: REMOVE_HIGHLIGHT_COLOR,
				inputMethod: getInputMethodFromParentKeys(parents),
			})({ tr });

			return tr;
		});

		closeMenu?.(event);
	};

	return (
		<div css={styles.removeColorButton}>
			<Button shouldFitContainer appearance="subtle" onClick={onClick}>
				<Text weight="medium">{formatMessage(messages.removeColor)}</Text>
			</Button>
		</div>
	);
};
